import { Injectable } from '@angular/core';
import { IServerResponse } from '@thx/interfaces';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SEND_MESSAGE } from './chat.gql';
import { Apollo } from 'apollo-angular';
import { IChatResponse } from '../../interfaces/chat-response.interface';
import { convict } from '../../config/convict-env.config';

@Injectable({ providedIn: 'root' })
export class ChatService {
  constructor(private readonly _apollo: Apollo) {}

  sendMessage(message: string, sessionId: string): Observable<IServerResponse<IChatResponse>> {
    return this._apollo
      .query<{ getChatResponse: IChatResponse }>({
        query: SEND_MESSAGE,
        variables: { input: { userMessage: message, sessionId: sessionId } },
      })
      .pipe(
        map((response) => {
          return { data: response?.data?.getChatResponse, loading: false };
        }),
        catchError((error: any): Observable<IServerResponse<IChatResponse>> => of({ error: error, loading: false })),
      );
  }

  /**
   * Stream chat messages from the server with abort support
   * Returns an Observable that emits each chunk as it arrives (SSE compliant parsing)
   */
  streamMessageWithFetch(message: string, sessionId: string, signal?: AbortSignal): Observable<string> {
    return new Observable((observer) => {
      const baseUrl = `https://${convict?.javaChatBotAppRunnerUrl}`;
      const url = new URL('/ai/chat', baseUrl).toString();

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream'
        },
        body: JSON.stringify({ userMessage: message, sessionId: sessionId }),
        signal: signal, // Pass the abort signal
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
          }

          if (!response.body) {
            throw new Error('Response body is empty');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let eventData: string | null = null;

          const processLine = (rawLine: string) => {
            // Remove trailing \r if present
            const line = rawLine.replace(/\r$/, '');

            if (line === '') {
              // End of one SSE event
              if (eventData != null) {
                observer.next(eventData);
                eventData = null;
              }
              return;
            }

            // Ignore comments starting with ':'
            if (line.startsWith(':')) {
              return;
            }

            // Handle data field: allow optional single space after colon
            if (line.startsWith('data:')) {
              const value = line.length >= 5 && line[5] === ' ' ? line.substring(6) : line.substring(5);
              if (eventData == null) {
                eventData = value;
              } else {
                // Multi-line data: each data field appends a newline per SSE spec
                eventData += '\n' + value;
              }
            }
            // We can add handling for event:, id:, retry: if needed
          };

          const read = () => {
            reader.read().then(({ done, value }) => {
              if (done) {
                // Flush any pending event on stream end
                if (eventData != null) {
                  observer.next(eventData);
                  eventData = null;
                }
                observer.complete();
                return;
              }

              const chunk = decoder.decode(value, { stream: true });
              if (chunk) {
                buffer += chunk;

                // Split by \n but keep last partial line in buffer
                const parts = buffer.split('\n');
                // All complete lines except the last
                for (let i = 0; i < parts.length - 1; i++) {
                  processLine(parts[i]);
                }
                // Keep the last (possibly incomplete) line in buffer
                buffer = parts[parts.length - 1];
              }
              read();
            });
          };

          read();
        })
        .catch((error) => {
          // Distinguish between abort and other errors
          if ((error as any).name === 'AbortError') {
            console.log('Stream aborted by user');
            observer.complete();
          } else {
            console.error('Fetch error:', error);
            observer.error(error);
          }
        });

      // Cleanup
      return () => {
        // Nothing specific: the supplied AbortSignal controls fetch cancellation
      };
    });
  }
}
